import {
  QUAOM_PUBLIC_OBSERVABILITY_ALLOWED_TAGS,
  QUAOM_PUBLIC_OBSERVABILITY_FORBIDDEN_FIELDS,
  QUAOM_PUBLIC_OBSERVABILITY_METRICS,
  type QuaomPublicObservabilityMetricName,
  type QuaomPublicObservabilityUnit,
} from '@quaom/contracts/observability';
import { deepRedactSensitiveValues, sanitizeUrlForPublicSinks } from '../security/redaction';
import type { PublicRouteMatch } from '../core/types';

export type PublicObservabilityMetric = {
  name: QuaomPublicObservabilityMetricName;
  value: number;
  unit: QuaomPublicObservabilityUnit;
  tags?: Record<string, string | number | boolean>;
};

export type PublicObservability = {
  emitMetric(metric: PublicObservabilityMetric): void;
  counter(name: QuaomPublicObservabilityMetricName, tags?: Record<string, unknown>): void;
  duration(
    name: QuaomPublicObservabilityMetricName,
    durationMs: number,
    tags?: Record<string, unknown>,
  ): void;
};

const ALLOWED_METRIC_NAMES = new Set<string>(QUAOM_PUBLIC_OBSERVABILITY_METRICS);
const ALLOWED_TAGS = new Set<string>(QUAOM_PUBLIC_OBSERVABILITY_ALLOWED_TAGS);
const FORBIDDEN_FIELDS = new Set<string>(
  QUAOM_PUBLIC_OBSERVABILITY_FORBIDDEN_FIELDS.map((field) => String(field).toLowerCase()),
);

export function createPublicObservability(win: Window = window): PublicObservability {
  return {
    emitMetric(metric) {
      const sanitized = sanitizePublicMetric(metric);
      win.dispatchEvent(new CustomEvent('quaom:public-observability', { detail: sanitized }));
    },
    counter(name, tags) {
      this.emitMetric({ name, value: 1, unit: 'count', tags: sanitizeMetricTags(tags) });
    },
    duration(name, durationMs, tags) {
      this.emitMetric({
        name,
        value: Math.max(0, Math.round(durationMs)),
        unit: 'ms',
        tags: sanitizeMetricTags(tags),
      });
    },
  };
}

export function sanitizePublicMetric(metric: PublicObservabilityMetric): PublicObservabilityMetric {
  const name = ALLOWED_METRIC_NAMES.has(metric.name) ? metric.name : 'public_runtime_error_total';
  return {
    name,
    value: Number.isFinite(metric.value) ? metric.value : 0,
    unit: metric.unit,
    tags: sanitizeMetricTags(metric.tags),
  };
}

export function sanitizeMetricTags(
  tags?: Record<string, unknown>,
): Record<string, string | number | boolean> | undefined {
  if (!tags) return undefined;
  const redacted = deepRedactSensitiveValues(tags) as Record<string, unknown>;
  const result: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(redacted)) {
    if (!ALLOWED_TAGS.has(key)) continue;
    if (FORBIDDEN_FIELDS.has(key.toLowerCase())) continue;
    if (typeof value === 'string') {
      result[key] = normalizeRouteTemplate(sanitizeUrlForPublicSinks(value));
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      result[key] = value;
    }
  }
  return Object.keys(result).length ? result : undefined;
}

export function routeObservabilityTags(route: PublicRouteMatch): Record<string, string | boolean> {
  return {
    pageId: route.id,
    routeKind: route.kind,
    routeOwner: route.owner,
    routeTemplate: routeTemplateFor(route),
    ok: true,
  };
}

export function routeTemplateFor(route: PublicRouteMatch): string {
  if (route.kind === 'dynamic-text' || route.id === 'space-text') {
    const hasPublicId = Boolean(route.params?.publicId);
    if (!hasPublicId) return route.lang && route.lang !== 'en' ? '/:lang/t' : '/t';
    return route.lang && route.lang !== 'en' ? '/:lang/t/:publicId' : '/t/:publicId';
  }
  if (route.kind === 'static') {
    return route.lang && route.lang !== 'en'
      ? `/:lang/${route.slug || ''}`.replace(/\/$/, '/')
      : `/${route.slug || ''}`.replace(/\/$/, '/');
  }
  if (route.slug) {
    return route.lang && route.lang !== 'en' ? `/:lang/${route.slug}` : `/${route.slug}`;
  }
  return route.kind === 'unknown' ? 'unknown' : '/';
}

export function normalizeRouteTemplate(value: string): string {
  const withoutHash = String(value || '').split('#')[0];
  const url = safeUrl(withoutHash);
  const pathname = url ? url.pathname : withoutHash.split('?')[0];
  const normalized = pathname
    .replace(/\/([a-z]{2})\/t\/[^/?#]+/i, '/:lang/t/:publicId')
    .replace(/\/t\/[^/?#]+/i, '/t/:publicId')
    .replace(/\/space\/texts\/[^/?#]+/i, '/space/texts/:publicId')
    .replace(/\/api\/space\/texts\/[^/?#]+/i, '/api/space/texts/:publicId');
  if (normalized !== pathname) return normalized;
  return sanitizeUrlForPublicSinks(value);
}

function safeUrl(value: string): URL | null {
  try {
    return new URL(value, 'https://quaom.invalid');
  } catch {
    return null;
  }
}
